import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Zap,
  Lightbulb,
  Plug,
  AlertCircle,
  ShieldCheck,
  Settings,
  Calendar,
  Cpu,
  Wrench,
  Package,
  Clock,
  Cog,
  AlignVerticalJustifyCenter,
  Search,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import SEO from "../Components/SEO";

const electricalServices = [
  {
    id: "e1",
    slug: "installations-upgrades",
    title: "Installations & Upgrades",
    icon: Zap,
    desc: "Complete electrical installation and upgrade services for residential, commercial, and industrial properties.",
    details: {
      overview:
        "Professional electrical installation and upgrade services ensuring safety, compliance, and efficiency across all project types. We handle everything from new installations to complete system upgrades.",
      features: [
        "New electrical installations from ground up",
        "Distribution board upgrades and replacements",
        "Circuit breaker installations and upgrades",
        "Smart home integration and automation",
        "Energy-efficient electrical solutions",
        "Code-compliant work adhering to safety standards",
      ],
      industries: ["Residential", "Commercial", "Industrial", "Retail"],
      process:
        "Consultation → Site Assessment → Planning → Installation → Testing & Certification",
    },
  },
  {
    id: "e2",
    slug: "lighting-solutions",
    title: "Lighting Solutions",
    icon: Lightbulb,
    desc: "Innovative lighting design and installation to enhance your space with energy-efficient and aesthetic solutions.",
    details: {
      overview:
        "Modern lighting design and installation that combines aesthetics with energy efficiency to create the perfect ambiance while reducing operational costs and environmental impact.",
      features: [
        "LED lighting installations for maximum energy savings",
        "Outdoor lighting systems and security lighting",
        "Emergency lighting and exit signs",
        "Custom lighting design consultation",
        "Automated lighting controls and sensors",
        "Retrofit solutions for existing fixtures",
      ],
      industries: [
        "Offices",
        "Warehouses",
        "Retail",
        "Hospitality",
        "Residential",
      ],
      process:
        "Lighting Assessment → Design Proposal → Product Selection → Installation → Optimization",
    },
  },
  {
    id: "e3",
    slug: "rewiring-power-points",
    title: "Rewiring & Power Point Installation",
    icon: Plug,
    desc: "Professional rewiring services and power point installations to meet your growing electrical demands safely.",
    details: {
      overview:
        "Comprehensive rewiring and power point installation services to modernize your electrical infrastructure and accommodate increased power requirements safely and efficiently.",
      features: [
        "Complete property rewiring projects",
        "Additional power point installations",
        "USB charging outlet installations",
        "Dedicated circuits for high-power appliances",
        "Cable management and concealment solutions",
        "Upgrades to meet current electrical codes",
      ],
      industries: [
        "Residential",
        "Commercial",
        "Industrial",
        "Renovation Projects",
      ],
      process:
        "Electrical Assessment → Load Calculation → Design → Rewiring/Installation → Testing & Certification",
    },
  },
  {
    id: "e4",
    slug: "fault-finding-emergency-repairs",
    title: "Fault Finding & Emergency Repairs",
    icon: AlertCircle,
    desc: "24/7 emergency electrical services with rapid response times to diagnose and repair electrical faults.",
    details: {
      overview:
        "Rapid response electrical troubleshooting and emergency repair services using advanced diagnostic equipment to minimize downtime and restore operations quickly and safely.",
      features: [
        "24/7 emergency callout services available",
        "Advanced diagnostic and testing equipment",
        "Power outage resolution and restoration",
        "Safety hazard identification and elimination",
        "Same-day repairs for urgent issues",
        "Root cause analysis and reporting",
      ],
      industries: ["All sectors requiring electrical support"],
      process:
        "Emergency Response → Fault Diagnosis → Repair Execution → System Testing → Documentation",
    },
  },
  {
    id: "e5",
    slug: "compliance-inspections",
    title: "Compliance Inspections & Safety Certification",
    icon: ShieldCheck,
    desc: "Comprehensive electrical safety inspections and certification to ensure compliance with regulations.",
    details: {
      overview:
        "Thorough electrical safety inspections and compliance certification services ensuring your installations meet all regulatory requirements and industry safety standards.",
      features: [
        "Certificate of Compliance (CoC) issuance",
        "Comprehensive electrical safety audits",
        "Pre-purchase property inspections",
        "Insurance assessment and documentation",
        "Regulatory compliance reports and records",
        "Ongoing compliance monitoring programs",
      ],
      industries: ["Residential", "Commercial", "Industrial", "Real Estate"],
      process:
        "Initial Inspection → Testing & Measurements → Report Generation → Certification → Recommendations",
    },
  },
  {
    id: "e6",
    slug: "instrumentation-control-wiring",
    title: "Instrumentation & Control Wiring",
    icon: Settings,
    desc: "Specialized instrumentation and control system wiring for industrial automation and process control.",
    details: {
      overview:
        "Expert instrumentation and control wiring services for industrial automation systems, ensuring precise signal transmission and reliable process control operations.",
      features: [
        "PLC wiring and system integration",
        "Sensor and transmitter installations",
        "Control panel assembly and wiring",
        "Process automation wiring solutions",
        "SCADA system integration support",
        "Signal and data cable installations",
      ],
      industries: [
        "Manufacturing",
        "Processing",
        "Mining",
        "Utilities",
        "Automation",
      ],
      process:
        "System Analysis → Design & Planning → Panel Assembly → Field Wiring → Testing & Commissioning",
    },
  },
  {
    id: "e7",
    slug: "preventive-maintenance",
    title: "Preventive & Scheduled Maintenance",
    icon: Calendar,
    desc: "Proactive maintenance programs to prevent electrical failures and extend equipment lifespan.",
    details: {
      overview:
        "Scheduled preventive maintenance programs designed to identify potential issues before they cause failures, ensuring continuous operations and extending equipment lifespan.",
      features: [
        "Regular inspection and testing schedules",
        "Thermal imaging surveys for hotspot detection",
        "Connection tightening and cleaning",
        "Comprehensive equipment testing programs",
        "Detailed maintenance reports and logs",
        "Predictive maintenance recommendations",
      ],
      industries: ["All sectors with electrical infrastructure"],
      process:
        "Initial Audit → Maintenance Plan Development → Scheduled Service → Performance Monitoring → Continuous Improvement",
    },
  },
  {
    id: "e8",
    slug: "industrial-machine-wiring",
    title: "Industrial Machine Wiring & Troubleshooting",
    icon: Cpu,
    desc: "Expert machine wiring and troubleshooting services for industrial equipment and production lines.",
    details: {
      overview:
        "Specialized industrial machine wiring and troubleshooting services ensuring reliable operation of production equipment and minimizing costly downtime.",
      features: [
        "Complete machine electrical installations",
        "Motor control center wiring and setup",
        "Variable frequency drive installations",
        "Production line electrical integration",
        "Machine troubleshooting and repairs",
        "Downtime minimization strategies",
      ],
      industries: [
        "Manufacturing",
        "Processing",
        "Packaging",
        "Assembly Lines",
      ],
      process:
        "Requirements Review → Wiring Design → Installation → Testing → Commissioning & Training",
    },
  },
];

const mechanicalServices = [
  {
    id: "m1",
    slug: "field-service-support",
    title: "Field Service Support",
    icon: Wrench,
    desc: "On-site mechanical support services for immediate assistance with equipment and machinery issues.",
    details: {
      overview:
        "Rapid on-site mechanical support services providing immediate assistance for equipment breakdowns and maintenance needs, minimizing downtime and production losses.",
      features: [
        "On-site technical support and assistance",
        "Emergency breakdown response teams",
        "Mobile workshop services for remote sites",
        "Remote location support capabilities",
        "Rapid response teams available 24/7",
        "Preventative maintenance on-site",
      ],
      industries: [
        "Mining",
        "Construction",
        "Agriculture",
        "Manufacturing",
        "Logistics",
      ],
      process:
        "Service Request → Rapid Deployment → On-Site Assessment → Repair/Maintenance → Documentation",
    },
  },
  {
    id: "m2",
    slug: "equipment-installations",
    title: "Equipment & System Installations",
    icon: Package,
    desc: "Professional installation of mechanical equipment and systems with precision and expertise.",
    details: {
      overview:
        "Expert mechanical equipment and system installation services ensuring proper setup, alignment, and commissioning for optimal performance and longevity.",
      features: [
        "Industrial machinery installations",
        "Conveyor system setup and integration",
        "Pump and motor installations",
        "Hydraulic and pneumatic systems",
        "Equipment commissioning and testing",
        "Operator training and documentation",
      ],
      industries: [
        "Manufacturing",
        "Mining",
        "Logistics",
        "Processing",
        "Infrastructure",
      ],
      process:
        "Site Preparation → Equipment Positioning → Installation → Alignment → Testing & Commissioning",
    },
  },
  {
    id: "m3",
    slug: "scheduled-equipment-servicing",
    title: "Scheduled Equipment Servicing",
    icon: Clock,
    desc: "Regular servicing programs to maintain optimal performance and reliability of your equipment.",
    details: {
      overview:
        "Comprehensive scheduled servicing programs designed to keep your equipment running at peak performance while preventing unexpected failures and costly downtime.",
      features: [
        "Planned preventative maintenance schedules",
        "Lubrication services and fluid changes",
        "Wear component replacements",
        "Performance optimization and tuning",
        "Service history tracking and reporting",
        "Predictive maintenance recommendations",
      ],
      industries: [
        "Manufacturing",
        "Mining",
        "Processing",
        "Transportation",
        "Agriculture",
      ],
      process:
        "Equipment Assessment → Schedule Development → Service Execution → Performance Testing → Documentation",
    },
  },
  {
    id: "m4",
    slug: "general-maintenance-repairs",
    title: "General Maintenance & Repairs",
    icon: Cog,
    desc: "Comprehensive mechanical maintenance and repair services for all types of industrial equipment.",
    details: {
      overview:
        "Full-service mechanical maintenance and repair capabilities covering all aspects of industrial equipment, from routine maintenance to complex repairs and modifications.",
      features: [
        "Emergency and routine equipment repairs",
        "Bearing replacements and refurbishment",
        "Belt and chain servicing and replacement",
        "Gearbox maintenance and repairs",
        "Welding and fabrication services",
        "Component machining and modification",
      ],
      industries: ["All industrial and commercial sectors"],
      process:
        "Fault Assessment → Repair Planning → Parts Procurement → Repair Execution → Testing & Validation",
    },
  },
  {
    id: "m5",
    slug: "precision-alignment",
    title: "Precision Alignment",
    icon: AlignVerticalJustifyCenter,
    desc: "Laser-precision alignment services to reduce wear, vibration, and energy consumption.",
    details: {
      overview:
        "State-of-the-art laser alignment services ensuring optimal equipment performance, reducing vibration, extending component life, and improving energy efficiency.",
      features: [
        "Laser shaft alignment for rotating equipment",
        "Belt alignment and tensioning services",
        "Coupling alignment and installation",
        "Pulley and sheave alignment",
        "Vibration analysis and diagnostics",
        "Alignment documentation and reporting",
      ],
      industries: [
        "Manufacturing",
        "Power Generation",
        "Mining",
        "Processing",
        "Utilities",
      ],
      process:
        "Pre-Alignment Assessment → Laser Measurement → Adjustment & Correction → Verification → Documentation",
    },
  },
  {
    id: "m6",
    slug: "mechanical-fault-finding",
    title: "Mechanical Fault Finding",
    icon: Search,
    desc: "Advanced diagnostic services to identify and resolve mechanical issues efficiently.",
    details: {
      overview:
        "Comprehensive mechanical fault finding services using advanced diagnostic techniques to quickly identify root causes and implement effective solutions, minimizing downtime.",
      features: [
        "Root cause analysis and investigation",
        "Vibration monitoring and analysis",
        "Thermal imaging inspections",
        "Performance testing and evaluation",
        "Diagnostic reporting and recommendations",
        "Failure mode analysis",
      ],
      industries: [
        "Manufacturing",
        "Mining",
        "Processing",
        "Power Generation",
        "Transport",
      ],
      process:
        "Problem Identification → Diagnostic Testing → Root Cause Analysis → Solution Development → Implementation & Verification",
    },
  },
];

const ServicesPage = () => {
  const [expandedService, setExpandedService] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;
    const slug = hash.replace("#", "");
    const all = [...electricalServices, ...mechanicalServices];
    const matched = all.find((s) => s.slug === slug);
    if (matched) {
      setExpandedService(matched.id);
      setTimeout(() => {
        const el = document.getElementById(slug);
        if (el)
          window.scrollTo({ top: el.offsetTop - 100, behavior: "smooth" });
      }, 100);
    }
  }, []);

  const toggleService = (serviceId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setExpandedService((prev) => {
      const next = prev === serviceId ? null : serviceId;
      if (next === serviceId) {
        setTimeout(() => {
          const slug = [...electricalServices, ...mechanicalServices].find(
            (s) => s.id === serviceId,
          )?.slug;
          const el = document.getElementById(slug);
          if (el)
            window.scrollTo({
              top: el.getBoundingClientRect().top + window.scrollY - 100,
              behavior: "smooth",
            });
        }, 50);
      }
      return next;
    });
  };

  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500&display=swap');

    .sp-root { font-family: 'DM Sans', sans-serif; background: #fafaf8; }

    /* ── Hero ── */
    .sp-hero {
      background: #111; border-bottom: 1px solid rgba(255,255,255,0.06);
      padding: 10rem 7vw 7rem; position: relative; overflow: hidden;
    }
    .sp-hero-watermark {
      position: absolute; top: 50%; right: 5vw; transform: translateY(-50%);
      font-family: 'DM Serif Display', serif;
      font-size: clamp(6rem, 18vw, 16rem);
      color: rgba(255,255,255,0.025); line-height: 1;
      user-select: none; pointer-events: none; white-space: nowrap;
    }
    .sp-hero-content { position: relative; z-index: 1; max-width: 700px; }
    .sp-eyebrow { display: flex; align-items: center; gap: 0.75rem; margin-bottom: 2rem; }
    .sp-eyebrow-line { width: 28px; height: 1px; background: #E78730; flex-shrink: 0; }
    .sp-eyebrow-text { font-size: 0.68rem; letter-spacing: 0.26em; text-transform: uppercase; color: #E78730; font-weight: 500; }
    .sp-hero h1 { font-family: 'DM Serif Display', serif; font-size: clamp(3rem, 7vw, 6rem); line-height: 0.95; color: #fff; margin-bottom: 2rem; }
    .sp-hero h1 em { color: #E78730; font-style: italic; }
    .sp-hero-desc { font-size: 0.95rem; color: rgba(255,255,255,0.38); line-height: 1.8; max-width: 52ch; font-weight: 300; margin-bottom: 3rem; }
    .sp-hero-btns { display: flex; gap: 1rem; flex-wrap: wrap; }
    .sp-btn-primary { display: inline-block; background: #E78730; color: #fff; font-size: 0.7rem; font-weight: 500; letter-spacing: 0.18em; text-transform: uppercase; text-decoration: none; padding: 0.9rem 2.25rem; font-family: 'DM Sans', sans-serif; transition: opacity 0.2s; }
    .sp-btn-primary:hover { opacity: 0.82; }
    .sp-btn-ghost { display: inline-block; border: 1px solid rgba(255,255,255,0.15); color: rgba(255,255,255,0.55); font-size: 0.7rem; font-weight: 400; letter-spacing: 0.18em; text-transform: uppercase; text-decoration: none; padding: 0.9rem 2.25rem; font-family: 'DM Sans', sans-serif; transition: border-color 0.25s, color 0.25s; }
    .sp-btn-ghost:hover { border-color: rgba(255,255,255,0.4); color: #fff; }

    /* ── Body ── */
    .sp-body { padding: 0 7vw 6rem; }

    /* ── Division header ── */
    .sp-division { padding-top: 6rem; margin-bottom: 2rem; scroll-margin-top: 100px; }
    .sp-division-header { display: flex; align-items: flex-end; justify-content: space-between; gap: 2rem; flex-wrap: wrap; padding-bottom: 2.5rem; border-bottom: 1px solid #e8e8e4; margin-bottom: 0; }
    .sp-division-left {}
    .sp-division-icon { color: #E78730; margin-bottom: 1rem; opacity: 0.8; }
    .sp-division h2 { font-family: 'DM Serif Display', serif; font-size: clamp(2rem, 4vw, 3rem); color: #1a1a1a; line-height: 1.05; margin-bottom: 0; }
    .sp-division h2 em { color: #E78730; font-style: italic; }
    .sp-division-desc { font-size: 0.9rem; color: #888; line-height: 1.8; font-weight: 300; max-width: 50ch; }

    /* ── Service rows (accordion) ── */
    .sp-service-list { border-left: 1px solid #e8e8e4; border-right: 1px solid #e8e8e4; }

    .sp-card {
      border-bottom: 1px solid #e8e8e4;
      background: #fafaf8;
      transition: background 0.2s;
    }
    .sp-card:first-child { border-top: 1px solid #e8e8e4; }
    .sp-card.expanded { background: #fff; }

    .sp-card-trigger {
      display: flex; align-items: center; justify-content: space-between;
      padding: 2rem 2.5rem; cursor: pointer; gap: 1.5rem;
    }

    .sp-card-left { display: flex; align-items: center; gap: 1.75rem; flex: 1; min-width: 0; }

    .sp-card-icon-wrap {
      width: 42px; height: 42px; display: flex; align-items: center; justify-content: center;
      flex-shrink: 0; border: 1px solid #e8e8e4; background: #fff;
      transition: border-color 0.2s, background 0.2s;
    }
    .sp-card.expanded .sp-card-icon-wrap { border-color: #E78730; background: rgba(231,135,48,0.05); }

    .sp-card-text {}
    .sp-card-title { font-size: 1rem; font-weight: 400; color: #1a1a1a; margin-bottom: 0.2rem; line-height: 1.3; }
    .sp-card-desc { font-size: 0.8rem; color: #aaa; font-weight: 300; line-height: 1.5; }

    .sp-card-toggle {
      color: #ccc; flex-shrink: 0; transition: color 0.2s;
      background: none; border: none; cursor: pointer; padding: 0.25rem;
      display: flex; align-items: center;
    }
    .sp-card.expanded .sp-card-toggle { color: #E78730; }

    /* Expanded content */
    .sp-card-body { padding: 0 2.5rem 2.5rem; }
    .sp-card-divider { border: none; border-top: 1px solid #f0ede8; margin-bottom: 2.5rem; }

    .sp-detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 2.5rem; margin-bottom: 2.5rem; }

    .sp-detail-label {
      font-size: 0.62rem; letter-spacing: 0.22em; text-transform: uppercase;
      color: #E78730; font-weight: 500; margin-bottom: 1rem;
      display: flex; align-items: center; gap: 0.5rem;
    }
    .sp-detail-label::before { content: ''; display: block; width: 12px; height: 1px; background: #E78730; flex-shrink: 0; }

    .sp-overview { font-size: 0.875rem; color: #666; line-height: 1.85; font-weight: 300; }

    .sp-features { list-style: none; padding: 0; margin: 0; }
    .sp-feature { display: flex; align-items: flex-start; gap: 0.75rem; margin-bottom: 0.6rem; font-size: 0.85rem; color: #555; font-weight: 300; line-height: 1.5; }
    .sp-feature-dot { width: 4px; height: 4px; background: #E78730; border-radius: 50%; flex-shrink: 0; margin-top: 7px; }

    .sp-industries { display: flex; flex-wrap: wrap; gap: 0.5rem; }
    .sp-industry-tag {
      font-size: 0.65rem; letter-spacing: 0.1em; text-transform: uppercase;
      color: #888; border: 1px solid #e8e8e4; padding: 0.3rem 0.75rem; font-weight: 400;
    }

    .sp-process-wrap { background: #f4f1ec; padding: 1.25rem 1.5rem; }
    .sp-process-text { font-size: 0.82rem; color: #666; font-weight: 300; line-height: 1.7; }

    /* ── CTA section ── */
    .sp-cta {
      background: #111; margin-top: 6rem;
      display: grid; grid-template-columns: 1fr 1fr;
      border-top: 1px solid rgba(255,255,255,0.06);
    }
    .sp-cta-left { padding: 6rem 7vw; border-right: 1px solid rgba(255,255,255,0.06); }
    .sp-cta h2 { font-family: 'DM Serif Display', serif; font-size: clamp(2rem, 4vw, 3rem); color: #fff; line-height: 1.05; margin-bottom: 1.5rem; }
    .sp-cta h2 em { color: #E78730; font-style: italic; }
    .sp-cta p { font-size: 0.93rem; color: rgba(255,255,255,0.38); line-height: 1.85; font-weight: 300; max-width: 44ch; margin-bottom: 2.5rem; }
    .sp-cta-btns { display: flex; gap: 1rem; flex-wrap: wrap; }
    .sp-cta-right { padding: 6rem 7vw; display: flex; flex-direction: column; justify-content: center; }
    .sp-cta-stat { padding: 2rem 0; border-bottom: 1px solid rgba(255,255,255,0.06); }
    .sp-cta-stat:first-child { padding-top: 0; }
    .sp-cta-stat:last-child { border-bottom: none; padding-bottom: 0; }
    .sp-cta-stat-num { font-family: 'DM Serif Display', serif; font-size: 2.5rem; color: #fff; line-height: 1; margin-bottom: 0.3rem; }
    .sp-cta-stat-num span { color: #E78730; }
    .sp-cta-stat-label { font-size: 0.65rem; letter-spacing: 0.18em; text-transform: uppercase; color: rgba(255,255,255,0.3); font-weight: 400; }

    /* Responsive */
    @media (max-width: 900px) {
      .sp-detail-grid { grid-template-columns: 1fr; gap: 2rem; }
      .sp-cta { grid-template-columns: 1fr; }
      .sp-cta-left { border-right: none; border-bottom: 1px solid rgba(255,255,255,0.06); padding: 5rem 7vw; }
      .sp-cta-right { padding: 4rem 7vw; }
      .sp-division-header { flex-direction: column; align-items: flex-start; }
    }

    @media (max-width: 600px) {
      .sp-card-trigger { padding: 1.5rem; }
      .sp-card-body { padding: 0 1.5rem 2rem; }
      .sp-card-left { gap: 1rem; }
    }
  `;

  const ServiceCard = ({ service }) => {
    const isExpanded = expandedService === service.id;
    const Icon = service.icon;
    const isElectrical = service.id.startsWith("e");

    return (
      <div
        id={service.slug}
        className={`sp-card${isExpanded ? " expanded" : ""}`}
      >
        <div
          className="sp-card-trigger"
          onClick={(e) => toggleService(service.id, e)}
        >
          <div className="sp-card-left">
            <div className="sp-card-icon-wrap">
              <Icon
                size={18}
                strokeWidth={1.25}
                color={isElectrical ? "#6b8cba" : "#E78730"}
              />
            </div>
            <div className="sp-card-text">
              <div className="sp-card-title">{service.title}</div>
              <div className="sp-card-desc">{service.desc}</div>
            </div>
          </div>
          <button
            className="sp-card-toggle"
            onClick={(e) => {
              e.stopPropagation();
              toggleService(service.id, e);
            }}
          >
            {isExpanded ? (
              <ChevronUp size={16} strokeWidth={1.5} />
            ) : (
              <ChevronDown size={16} strokeWidth={1.5} />
            )}
          </button>
        </div>

        {isExpanded && service.details && (
          <div className="sp-card-body">
            <hr className="sp-card-divider" />
            <div className="sp-detail-grid">
              <div>
                <div className="sp-detail-label">Overview</div>
                <p className="sp-overview">{service.details.overview}</p>
              </div>
              <div>
                <div className="sp-detail-label">What We Offer</div>
                <ul className="sp-features">
                  {service.details.features.map((f, i) => (
                    <li key={i} className="sp-feature">
                      <span className="sp-feature-dot" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div style={{ marginBottom: "1.75rem" }}>
              <div className="sp-detail-label">Industries</div>
              <div className="sp-industries">
                {service.details.industries.map((ind, i) => (
                  <span key={i} className="sp-industry-tag">
                    {ind}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <div className="sp-detail-label">Our Process</div>
              <div className="sp-process-wrap">
                <p className="sp-process-text">{service.details.process}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <SEO
        title="Our Services | Trademin Ltd"
        description="Explore Trademin Ltd's professional electrical and mechanical services."
        canonical="https://www.trademin.co.nz/our-services"
        ogTitle="Our Services | Trademin Ltd"
        ogDescription="Professional electrical and mechanical services by Trademin Ltd."
      />
      <style>{styles}</style>
      <div className="sp-root">
        {/* Hero */}
        <section className="sp-hero">
          <div className="sp-hero-watermark">SERVICES</div>
          <div className="sp-hero-content">
            <div className="sp-eyebrow">
              <span className="sp-eyebrow-line" />
              <span className="sp-eyebrow-text">What We Do</span>
            </div>
            <h1>
              Our <em>Services</em>
            </h1>
            <p className="sp-hero-desc">
              Professional electrical and mechanical solutions you can trust —
              from complex industrial installations to routine maintenance.
            </p>
            <div className="sp-hero-btns">
              <a href="#electrical" className="sp-btn-primary">
                Electrical Services
              </a>
              <a href="#mechanical" className="sp-btn-ghost">
                Mechanical Services
              </a>
            </div>
          </div>
        </section>

        {/* Body */}
        <div className="sp-body">
          {/* Electrical */}
          <div id="electrical" className="sp-division">
            <div className="sp-division-header">
              <div className="sp-division-left">
                <Zap
                  size={20}
                  strokeWidth={1.25}
                  className="sp-division-icon"
                />
                <h2>
                  Electrical <em>Services</em>
                </h2>
              </div>
              <p className="sp-division-desc">
                From power installations to industrial automation, our
                electrical services cover the full spectrum of commercial and
                industrial needs.
              </p>
            </div>
            <div className="sp-service-list">
              {electricalServices.map((s) => (
                <ServiceCard key={s.id} service={s} />
              ))}
            </div>
          </div>

          {/* Mechanical */}
          <div id="mechanical" className="sp-division">
            <div className="sp-division-header">
              <div className="sp-division-left">
                <Wrench
                  size={20}
                  strokeWidth={1.25}
                  className="sp-division-icon"
                />
                <h2>
                  Mechanical <em>Services</em>
                </h2>
              </div>
              <p className="sp-division-desc">
                Precision mechanical solutions including on-site support,
                installations, and comprehensive maintenance programs for all
                industrial applications.
              </p>
            </div>
            <div className="sp-service-list">
              {mechanicalServices.map((s) => (
                <ServiceCard key={s.id} service={s} />
              ))}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="sp-cta">
          <div className="sp-cta-left">
            <div className="sp-eyebrow" style={{ marginBottom: "1.5rem" }}>
              <span className="sp-eyebrow-line" />
              <span className="sp-eyebrow-text">Custom Solutions</span>
            </div>
            <h2>
              Need Something <em>Bespoke?</em>
            </h2>
            <p>
              Can't find exactly what you're looking for? We specialize in
              developing tailored solutions for unique electrical and mechanical
              challenges across all industries.
            </p>
            <div className="sp-cta-btns">
              <button
                className="sp-btn-primary"
                onClick={() => navigate("/request-quote")}
              >
                Request a Quote
              </button>
              <button
                className="sp-btn-ghost"
                style={{ background: "none" }}
                onClick={() => navigate("/contact-us")}
              >
                Contact Us
              </button>
            </div>
          </div>
          <div className="sp-cta-right">
            <div className="sp-cta-stat">
              <div className="sp-cta-stat-num">
                14<span>+</span>
              </div>
              <div className="sp-cta-stat-label">Services Offered</div>
            </div>
            <div className="sp-cta-stat">
              <div className="sp-cta-stat-num">
                2<span> Divisions</span>
              </div>
              <div className="sp-cta-stat-label">Electrical & Mechanical</div>
            </div>
            <div className="sp-cta-stat">
              <div className="sp-cta-stat-num">
                NZ<span>Wide</span>
              </div>
              <div className="sp-cta-stat-label">Service Coverage</div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ServicesPage;
